import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { IIndustry, IIndustryInput } from "models/industry";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  IndustryCells,
  addIndustry,
  deleteIndustry,
  editIndustry,
  getIndustries,
} from "framework/industries";
import AddIndustry from "./partials/AddIndustry/AddIndustry";
import BlockIcon from "@mui/icons-material/Block";
import EditIndustry from "./partials/EditIndustry/EditIndustry";
import DeleteIndustry from "./partials/DeleteIndustry/DeleteIndustry";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission } from "helpers";

interface Props {
  feature: Feature;
}

const Industries: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const { t, i18n } = useTranslation();
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [loading2, setLoading2] = useState<boolean>(false);
  const [Industries, setIndustries] = useState<IIndustry[]>([] as IIndustry[]);
  const [FilterIndustries, setFilterIndustries] = useState<IIndustry[]>(
    [] as IIndustry[]
  );
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedIndustry, setSelectedIndustry] = useState<IIndustry>(
    {} as IIndustry
  );
  const [inputObj, setInputObj] = useState<IIndustryInput>(
    {} as IIndustryInput
  );
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addIndustriesMutation = useMutation({
    mutationFn: (createInput: IIndustryInput) => {
      return addIndustry(createInput);
    },
  });

  const editIndustriesMutation = useMutation({
    mutationFn: (data: { createInput: IIndustryInput; id: string }) => {
      return editIndustry(data.createInput, data.id);
    },
  });

  const deleteIndustriesMutation = useMutation({
    mutationFn: (data: { id: string }) => {
      return deleteIndustry(data.id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery("get Industries", () =>
        getIndustries()
      );
      response =
        response && response.length > 0
          ? response.map((item: IIndustry) => ({
              ...item,
            }))
          : [];
      setIndustries(response);
      setFilterIndustries(response);
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(2);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Industry: IIndustry) => {
    setSelectedIndustry(Industry);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedIndustry({} as IIndustry);
  };

  const handleOpenDelete = (Industry: IIndustry) => {
    setSelectedIndustry(Industry);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedIndustry({} as IIndustry);
  };

  const getBodyCells = (row: IIndustry) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.name : row?.nameAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"right"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.name || !inputObj.nameAr)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addIndustriesMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Industry added successfully",
        type: "success",
      });
      fetchData();
      handleCloseAdd();
      setInputObj({} as IIndustryInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    // if ((!inputObj.name || selectedIndustry.name) && (!inputObj.NameAr || selectedIndustry.nameAr ))
    //   return setAlert({
    //     open: true,
    //     message: "Must provide a name",
    //     type: "error",
    //   });
    try {
      setLoading2(true);
      let createInput: IIndustryInput = {
        name: inputObj.name || selectedIndustry.name,
        nameAr: inputObj.nameAr || selectedIndustry.nameAr,
      };
      await editIndustriesMutation.mutateAsync({
        createInput,
        id: selectedIndustry.id,
      });
      setAlert({
        open: true,
        message: "Industry updated successfully",
        type: "success",
      });
      fetchData();
      handleCloseEdit();
      setInputObj({} as IIndustryInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteIndustriesMutation.mutateAsync({
        id: selectedIndustry.id,
      });
      setAlert({
        open: true,
        message: "Industry deleted successfully",
        type: "success",
      });
      fetchData();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterIndustries(Industries);
    let filtered = Industries.filter((city) =>
      city.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterIndustries(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add_industry")}>
                <IconButton
                  sx={{
                    // bgcolor: 'primary.main',
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterIndustries.length > 0 && (
          <List
            headCells={IndustryCells}
            data={FilterIndustries}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterIndustries || FilterIndustries.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_industry_header")}
              message={t("T_no_industry_message")}
            />
          )}
        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddIndustry
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditIndustry
          open={openEdit}
          loading={loading2}
          industry={selectedIndustry}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteIndustry
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Industries;
